import type { PropsWithChildren } from 'react';

import type { DisplayUnit, Nil, QuarterFraction } from '@core';
import { formatInches, FRACTION_MAP, isDisplayUnitInInches, isFiniteNumber } from '@core';

import { ComparisonIcon } from '../../icons';
import { Box } from '../../Layout';

import { QuarterFractions } from './Corridors/QuarterFractions';
import * as css from './ParameterCardValues.css';

export type ParameterCardValueProps = {
    displayUnit: DisplayUnit;
    value?: number | Nil;
    symbol?: string;
    indicatorColor?: 'green' | 'yellow' | 'red' | 'white';
    /**
     * Renders a stable-width value aligned to the right of its supplied space.
     * Use this when the value is the only child of ParameterCard.Values.
     */
    fixedWidth?: boolean;
};

export type ParameterCardComparisonProps = {
    displayUnit: DisplayUnit;
    value?: number | Nil;
    symbol?: string;
    showSymbol?: boolean;
};

export const ParameterCardValues = ({ children }: PropsWithChildren) => <Box className={css.values}>{children}</Box>;

export const ParameterCardValue = ({
    displayUnit,
    value,
    symbol,
    indicatorColor,
    fixedWidth,
}: ParameterCardValueProps) => (
    <Box
        display="flex"
        justifyContent={fixedWidth ? 'flex-end' : 'center'}
        flexDirection={isSpeedUnit(displayUnit) ? 'column' : 'row'}
        alignItems={displayUnit === 'deg' ? 'flex-start' : 'flex-end'}
        className={css.value({ indicatorColor, fixedWidth })}
    >
        {renderValue({ displayUnit, value, fixedWidth })}
        {symbol ? <span className={isSpeedUnit(displayUnit) ? css.speedUnit : css.symbol}>{symbol}</span> : null}
    </Box>
);

export const ParameterCardComparison = ({ displayUnit, value, symbol, showSymbol }: ParameterCardComparisonProps) => {
    if (!isFiniteNumber(value)) return null;

    return (
        <div className={css.comparison}>
            <ComparisonIcon width={14} />
            <div>
                {renderValue({ displayUnit, value })}
                {(showSymbol || showsComparisonSymbol(displayUnit)) && symbol ? (
                    <span className={css.symbol}>{symbol}</span>
                ) : null}
            </div>
        </div>
    );
};

const isSpeedUnit = (displayUnit: DisplayUnit) => displayUnit === 'km/h' || displayUnit === 'mph';

const showsComparisonSymbol = (displayUnit: DisplayUnit) => displayUnit === 'percent' || displayUnit === 'deg';

type RenderValueProps = {
    displayUnit: DisplayUnit;
    value?: number | Nil;
    fixedWidth?: boolean;
};

const renderValue = ({ displayUnit, value, fixedWidth }: RenderValueProps) => {
    if (!isFiniteNumber(value)) return <span>—</span>;
    if (isDisplayUnitInInches(displayUnit)) return renderInches(value, fixedWidth);

    return <span>{fixedWidth ? value?.toFixed(1) : value}</span>;
};

const renderInches = (value: number, fixedWidth?: boolean) => {
    const { integer, quarterFraction, isNegative } = formatInches(value);

    return (
        <span className={fixedWidth ? css.inchesValue : undefined}>
            <span className={fixedWidth ? css.inchesInteger : undefined}>
                {isNegative ? '-' : null}
                {integer}
            </span>
            <span className={fixedWidth ? css.inchesFraction : undefined}>
                <RenderFraction quarterFraction={quarterFraction} fixedWidth={fixedWidth} />
            </span>
        </span>
    );
};

type RenderFractionProps = {
    quarterFraction: QuarterFraction | null;
    fixedWidth?: boolean;
};

const RenderFraction = ({ quarterFraction, fixedWidth }: RenderFractionProps) => {
    if (quarterFraction === null) return null;
    if (fixedWidth) {
        return <QuarterFractions numerator={quarterFraction} />;
    }
    if (!quarterFraction) return null;
    return <span className={css.fraction}>{FRACTION_MAP[quarterFraction]}</span>;
};
